import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, Trophy, Medal, Star, Sparkles, Building2 } from 'lucide-react'

const awards = [
  {
    icon: Trophy,
    title: 'Best Government Technology Partner',
    year: '2023',
    organization: 'Maharashtra IT Excellence Awards',
    description: 'Recognized for outstanding contributions to government digital transformation initiatives.',
    color: '#f59e0b',
  },
  {
    icon: Medal,
    title: 'Excellence in Infrastructure Development',
    year: '2022',
    organization: 'Infrastructure India Summit',
    description: 'Awarded for delivering exceptional infrastructure projects across educational institutions.',
    color: '#6366f1',
  },
  {
    icon: Award,
    title: 'ISO 27001 Certification Achievement',
    year: '2021',
    organization: 'International Organization for Standardization',
    description: 'Achieved Information Security Management System certification for enterprise solutions.',
    color: '#10b981',
  },
  {
    icon: Star,
    title: 'Top 50 Growing Enterprises',
    year: '2022',
    organization: 'Business India Magazine',
    description: 'Featured among the fastest growing technology enterprises in Maharashtra.',
    color: '#ec4899',
  },
  {
    icon: Building2,
    title: 'Smart Education Initiative Award',
    year: '2023',
    organization: 'Digital India Conclave',
    description: 'Recognized for implementing smart classroom solutions in 100+ schools.',
    color: '#06b6d4',
  },
  {
    icon: Sparkles,
    title: 'Quality Excellence Award',
    year: '2021',
    organization: 'Quality Council of India',
    description: 'Honored for maintaining highest quality standards across all deliverables.',
    color: '#8b5cf6',
  },
]

const certifications = [
  { name: 'ISO 9001:2015 IAF-3', description: 'Quality Management System' },
  { name: 'ISO 14001:2015 IAF', description: 'Environmental Management' },
  { name: 'ISO 45001:2018 IAF', description: 'Occupational Health & Safety' },
  { name: 'ISO 50001', description: 'Energy Management' },
  { name: 'ISO 20000-1:2018', description: 'IT Service Management' },
  { name: 'ISO 27001:2013', description: 'Information Security' },
  { name: 'AIOTA', description: 'Industry Association Membership' },
  { name: 'BIFMA', description: 'Business Furniture Standards' },
  { name: 'Green Pro', description: 'Eco-Friendly Products' },
  { name: 'Green Guard', description: 'Environmental Certification' },
]

const Awards = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Awards & Recognition | Eonixa Dynamics - ISO Certified Excellence</title>
        <meta name="description" content="Eonixa Dynamics awards and certifications. ISO 9001, ISO 14001, ISO 45001, ISO 50001, ISO 20000, ISO 27001 certified. Recognized for excellence in government technology and infrastructure." />
        <meta name="keywords" content="ISO certifications, awards, recognition, quality excellence, government technology awards, infrastructure awards, Maharashtra IT awards" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.15) 100%)',
                border: '1px solid rgba(245, 158, 11, 0.25)',
              }}
            >
              <Trophy className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-slate-300">Excellence & Recognition</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Awards & <span className="gradient-text">Certifications</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Recognized for excellence in government technology, infrastructure development, and quality management.
              Our certifications reflect our commitment to delivering world-class solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Awards Grid */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Our Achievements</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Recognition from industry leaders and certifications from international bodies
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {awards.map((award, index) => {
              const Icon = award.icon
              return (
                <motion.div
                  key={award.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                        style={{
                          background: `${award.color}20`,
                          border: `1px solid ${award.color}40`,
                        }}
                      >
                        <Icon className="w-6 h-6" style={{ color: award.color }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-1">{award.title}</h3>
                        <span className="text-sm font-medium" style={{ color: award.color }}>{award.year}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-400 mb-3">{award.description}</p>
                    <p className="text-xs text-slate-500">{award.organization}</p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
              }}
            >
              <Award className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-300">Certified Excellence</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">10+ Certifications</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Internationally recognized certifications ensuring quality, security, and environmental compliance
            </p>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="text-center p-4 rounded-xl"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                }}
              >
                <p className="text-sm font-semibold text-white mb-1">{cert.name}</p>
                <p className="text-xs text-slate-400">{cert.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default Awards